/**
 * @file PaymentContext.tsx
 * @description PaymentProvider component and context (Subtask 11.2)
 *
 * Implements the PaymentProvider React component as specified in:
 * .taskmaster/docs/research/task-11-payment-context.md
 */

import React, { createContext, useReducer, useCallback, useMemo, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { paymentReducer, initialState } from './reducer';
import { clearAllSessions } from './persistence';
import { useUser } from '../../api/userService';
import type {
  PaymentContextValue,
  PaymentContextState,
  PaymentSession,
} from './types';
import type { Payment, PaymentStatus } from '../../types/payment';

/**
 * React Context for payment state management.
 * Default value is null - consumers must be wrapped in PaymentProvider.
 */
export const PaymentContext = createContext<PaymentContextValue | null>(null);

PaymentContext.displayName = 'PaymentContext';

/**
 * Props for PaymentProvider component.
 */
export interface PaymentProviderProps {
  children: ReactNode;
  /** Optional: Override storage key prefix (for testing) */
  storageKeyPrefix?: string;
  /** Optional: Disable persistence (for testing) */
  disablePersistence?: boolean;
}

/**
 * PaymentProvider component.
 * Wraps the React tree to provide payment state and actions to all descendants.
 */
export const PaymentProvider: React.FC<PaymentProviderProps> = ({
  children,
  // These props will be used in subtask 11.3 (persistence)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  storageKeyPrefix,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  disablePersistence,
}) => {
  const [state, dispatch] = useReducer(paymentReducer, initialState);
  // Task 37.4: Get userId from UserContext instead of localStorage
  const { isLoggedIn, userId: currentUserId } = useUser();
  
  // Track previous state for detecting logout/user-switch
  const prevStateRef = useRef<{ isLoggedIn: boolean; userId: string | number | null }>({
    isLoggedIn: false,
    userId: null,
  });
  
  /**
   * Effect: Handle logout and user switch cleanup (Task 34.6)
   * - On logout: clear all sessions for the current user
   * - On user switch: clear sessions for the previous user
   */
  useEffect(() => {
    const { isLoggedIn: wasLoggedIn, userId: prevUserId } = prevStateRef.current;

    // Detect logout: was logged in, now logged out
    if (wasLoggedIn && !isLoggedIn && prevUserId) {
      clearAllSessions(String(prevUserId));
      dispatch({ type: 'CLEAR' });
    }

    // Detect user switch: still logged in but different userId
    if (wasLoggedIn && isLoggedIn && prevUserId && currentUserId && prevUserId !== currentUserId) {
      clearAllSessions(String(prevUserId));
      dispatch({ type: 'CLEAR' });
    }

    // Update ref for next render
    prevStateRef.current = { isLoggedIn, userId: currentUserId };
  }, [isLoggedIn, currentUserId]);

  /**
   * Initialize a new payment session.
   * Note: Actual API integration will be added in subtask 11.4
   */
  const initPaymentSession = useCallback(
    async (
      tournamentId: string,
      paymentType: 'TOURNAMENT_CREATE_ENTRY' | 'TOURNAMENT_JOIN_ENTRY'
    ): Promise<Payment> => {
      dispatch({
        type: 'INIT_START',
        payload: { tournamentId, paymentType },
      });

      // Placeholder - actual implementation in subtask 11.4
      throw new Error('initPaymentSession not yet implemented (subtask 11.4)');
    },
    []
  );

  /**
   * Clear current payment session and reset state.
   */
  const clearPayment = useCallback(() => {
    dispatch({ type: 'CLEAR' });
  }, []);

  /**
   * Manually update payment status.
   */
  const setPaymentStatus = useCallback((status: PaymentStatus) => {
    dispatch({ type: 'STATUS_UPDATE', payload: { status } });
  }, []);

  /**
   * Start polling for payment status updates.
   * Note: Actual polling logic will be added in subtask 11.4
   */
  const startPolling = useCallback(() => {
    dispatch({ type: 'POLL_START' });
    // Placeholder - actual implementation in subtask 11.4
  }, []);

  /**
   * Stop polling for payment status updates.
   */
  const stopPolling = useCallback(() => {
    dispatch({ type: 'POLL_STOP' });
  }, []);

  /**
   * Retry a failed operation.
   * Note: Actual retry logic will be added in subtask 11.4
   */
  const retry = useCallback(async (): Promise<void> => {
    // Placeholder - actual implementation in subtask 11.4
    throw new Error('retry not yet implemented (subtask 11.4)');
  }, []);

  /**
   * Memoized context value to prevent unnecessary re-renders.
   */
  const contextValue = useMemo<PaymentContextValue>(
    () => ({
      // State
      session: state.session,
      paymentUIState: state.paymentUIState,
      isLoading: state.isLoading,
      isPolling: state.isPolling,
      currentStatus: state.currentStatus,
      error: state.error,
      pollCount: state.pollCount,
      // Actions
      initPaymentSession,
      clearPayment,
      setPaymentStatus,
      startPolling,
      stopPolling,
      retry,
    }),
    [
      state.session,
      state.paymentUIState,
      state.isLoading,
      state.isPolling,
      state.currentStatus,
      state.error,
      state.pollCount,
      initPaymentSession,
      clearPayment,
      setPaymentStatus,
      startPolling,
      stopPolling,
      retry,
    ]
  );

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
    </PaymentContext.Provider>
  );
};
